/* eslint-disable import/no-cycle */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import * as yup from 'yup';
import { postcodeValidator } from 'postcode-validator';

import styles from '@repo/ui-components/styles';
import Button from '@repo/ui-components/button/Button';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import breakpoints from '@repo/ui-components/breakpoints';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import Steps from './Steps';

import Questions from './modules/Questions';
import LeadCapture from './modules/LeadCapture';
import DealerLookup from './modules/DealerLookup';
import Recommendations from './modules/Recommendations';

const OuterContainer = styled.div`
  margin-block: ${styles.spacing['spacing-lg']};
  margin-inline: auto;
  max-width: ${breakpoints.md};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${styles.spacing['spacing-lg']};

  background-color: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.cardText};
`;

const QuizCardsContainer = styled.div`
  gap: ${styles.spacing['spacing-md']};
`;

const QuizCard = styled.div`
  display: ${({ active }) => (active ? 'flex' : 'none')};
  flex-direction: column;
  gap: ${styles.spacing['spacing-lg']};
`;

const ButtonsContainer = styled.div`
  display: flex;
  text-align: center;
  gap: ${styles.spacing['spacing-md']};

  button {
    width: 100%;
  }

  @media (min-width: ${breakpoints.md}) {
    button {
      width: auto;
    }
  }
`;

const splitFullName = fullName => {
  const nameParts = fullName.split(' ');

  if (nameParts.length >= 2) {
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    return {
      firstName,
      lastName,
    };
  }

  return {
    firstName: fullName,
    lastName: '',
  };
};

function BlockBuilderQuiz({ value, getInternalLink }) {
  const { tt } = useUiComponentsInternationalContext();

  const quizCards = [...(value?.cards || [])];
  const quizKey = value?._key;

  const [isLoading, setIsLoading] = useState(false);
  const [hasCompanyOwnedDealer, setHasCompanyOwnedDealer] = useState(false);

  const isBrowser = typeof window !== 'undefined';

  const leadFirstName =
    (isBrowser && sessionStorage?.getItem('leadFirstName')) || '';
  const leadLastName =
    (isBrowser && sessionStorage?.getItem('leadLastName')) || '';
  let leadName = '';

  if (leadFirstName) leadName = `${leadFirstName}`;
  if (leadLastName) leadName = `${leadName} ${leadLastName}`;

  const leadEmail = (isBrowser && sessionStorage?.getItem('leadEmail')) || '';
  const leadPostalCode =
    (isBrowser && sessionStorage?.getItem('leadPostalCode')) || '';

  const [name, setName] = useState(leadName || '');
  const [email, setEmail] = useState(leadEmail || '');
  const [postalCode, setPostalCode] = useState(leadPostalCode || '');
  const [errors, setErrors] = useState({});

  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [activeModule, setActiveModule] = useState(
    quizCards?.[currentSection]?.moduleType || 'None'
  );

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required(tt(s => s.blockBuilderCommon.nameRequiredValidationMessage)),
    email: yup
      .string()
      .email(tt(s => s.blockBuilderCommon.emailValidationMessage))
      .required(tt(s => s.blockBuilderCommon.emailRequiredValidationMessage)),
    postalCode: yup
      .string()
      .required(
        tt(s => s.blockBuilderCommon.postalCodeRequiredValidationMessage)
      )
      .min(
        5,
        tt(s => s.blockBuilderCommon.postalCodeLengthValidationMessage)
      ),
  });

  const handleBack = () => {
    const activeSection = currentSection - 1;
    setActiveModule(quizCards[activeSection].moduleType);
    setCurrentSection(activeSection);

    if (isBrowser) {
      sessionStorage?.setItem(
        `quizState_${quizKey}`,
        JSON.stringify({
          activeSection,
          activeModule: quizCards[activeSection].moduleType,
          answers,
          hasCompanyOwnedDealer: !!hasCompanyOwnedDealer,
        })
      );
    }
  };

  const getDealerSearchResults = async postCode => {
    try {
      const response = await fetch(
        `${process.env.GATSBY_POSTCODE_SEARCH_API}?postalCode=${postCode}`
      );

      const dealers = await response.json();
      const result = dealers?.some(dealer => dealer?.isCompanyOwned);

      return result;
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const handleLeadCapture = async (lName, lEmail, lPostalCode) => {
    setIsLoading(true);

    const fullName = splitFullName(lName);

    let response;

    try {
      response = await fetch(
        `${process.env.GATSBY_POSTCODE_SEARCH_API}?postalCode=${lPostalCode}`
      );
    } catch (error) {
      console.error(error);
    }

    const dealers = await response?.json();
    const result = dealers?.some(dealer => dealer?.isCompanyOwned);
    const activeSection = currentSection + 1;

    if (isBrowser) {
      sessionStorage?.setItem('leadFirstName', fullName.firstName);
      sessionStorage?.setItem('leadLastName', fullName.lastName);
      sessionStorage?.setItem('leadEmail', lEmail);
      sessionStorage?.setItem('leadPostalCode', lPostalCode);
      sessionStorage?.setItem('leadCompanyOwnedDealer', result);
    }

    const card = quizCards?.[currentSection];

    // Check if Capture Lead is enabled for this section
    if (card?.captureLeads) {
      // Check if we have both portalId and formId
      const portalId = process.env.GATSBY_HUBSPOT_PORTAL_ID;
      const formId = card?.captureLeadForm?.id;
      const fName = splitFullName(name);
      const hubspotutk = Cookies.get('hubspotutk') || '';

      // Try to submit request to the form
      if (portalId && formId) {
        const body = JSON.stringify({
          fields: [
            {
              objectTypeId: '0-1',
              name: 'email',
              value: email,
            },
            {
              objectTypeId: '0-1',
              name: 'firstname',
              value: fName.firstName,
            },
            {
              objectTypeId: '0-1',
              name: 'lastname',
              value: fName.lastName,
            },
            {
              objectTypeId: '0-1',
              name: 'full_name',
              value: name,
            },
            {
              objectTypeId: '0-1',
              name: 'zip',
              value: postalCode,
            },
          ],
          context: {
            hutk: hubspotutk,
            pageUri: window?.location?.href,
            pageName: document?.title,
          },
        });

        await fetch(
          `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body,
          }
        );
      }
    }

    setIsLoading(false);
    setHasCompanyOwnedDealer(result);
    setActiveModule(quizCards[activeSection].moduleType);
    setCurrentSection(activeSection);

    if (isBrowser) {
      sessionStorage?.setItem(
        `quizState_${quizKey}`,
        JSON.stringify({
          activeSection,
          activeModule: quizCards[activeSection].moduleType,
          answers,
          hasCompanyOwnedDealer: result,
        })
      );
    }
  };

  const handleForm = async event => {
    event.preventDefault();

    setErrors({});

    try {
      await validationSchema.validate(
        { name, email, postalCode },
        { abortEarly: false }
      );
      await handleLeadCapture(name, email, postalCode);
    } catch (error) {
      if (error.inner) {
        const validationErrors = {};
        error.inner.forEach(e => {
          validationErrors[e.path] = e.message;
        });
        setErrors(validationErrors);
      }
    }
  };

  const handleDealerLookup = async event => {
    event.preventDefault();

    setErrors({});

    try {
      let result;

      const isCAValid = postcodeValidator(postalCode.trim(), 'CA');
      const isUSValid = postcodeValidator(postalCode.trim(), 'US');

      // Check if valid US or CA postal code
      if (isCAValid || isUSValid) {
        setIsLoading(true);

        // If Canadian postal code, ensure the string is valid and has a space in the center
        if (isCAValid) {
          result = await getDealerSearchResults(
            postalCode
              .toUpperCase()
              .trim()
              .replace(/ /g, '')
              .replace(/(.{3})/, '$1 ')
          );
        } else {
          // Otherwise, send full zip code
          result = await getDealerSearchResults(postalCode);
        }

        setIsLoading(false);
      } else {
        setErrors({
          postalCode: 'Please enter a valid ZIP or Postal Code',
        });
        return;
      }

      const activeSection = currentSection + 1;

      setHasCompanyOwnedDealer(result);
      setActiveModule(quizCards[activeSection].moduleType);
      setCurrentSection(activeSection);

      if (isBrowser) {
        sessionStorage?.setItem(
          `quizState_${quizKey}`,
          JSON.stringify({
            activeSection,
            activeModule: quizCards[activeSection].moduleType,
            answers,
            hasCompanyOwnedDealer: result,
          })
        );
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setErrors({
        postalCode:
          'There was a problem while trying to verify postal code, please try again later.',
      });
    }
  };

  const handleNext = async event => {
    event.preventDefault();

    if (activeModule === 'Lead Capture') {
      await handleForm(event);
      return;
    }

    if (activeModule === 'Dealer Lookup') {
      await handleDealerLookup(event);
      return;
    }

    const activeSection = currentSection + 1;
    setActiveModule(quizCards[activeSection].moduleType);
    setCurrentSection(activeSection);

    if (isBrowser) {
      sessionStorage?.setItem(
        `quizState_${quizKey}`,
        JSON.stringify({
          activeSection,
          activeModule: quizCards[activeSection].moduleType,
          answers,
          hasCompanyOwnedDealer: !!hasCompanyOwnedDealer,
        })
      );
    }
  };

  const handleAnswerChange = (event, key, option) => {
    setAnswers(prevAnswers => ({
      ...prevAnswers,
      [currentSection]: {
        answer: event.target.value,
        option: option._id,
      },
    }));
  };

  useEffect(() => {
    if (activeModule === 'Question') {
      setNextButtonDisabled(answers?.[currentSection] === undefined);
    }

    if (activeModule === 'Lead Capture') {
      setNextButtonDisabled(false);
    }

    if (activeModule === 'Dealer Lookup') {
      setNextButtonDisabled(false);
    }
  }, [activeModule, currentSection, answers]);

  useEffect(() => {
    // Initialize quiz state from sessionStorage
    if (isBrowser) {
      const storedState = sessionStorage?.getItem(`quizState_${quizKey}`);
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        setActiveModule(parsedState.activeModule);
        setCurrentSection(parsedState.activeSection);
        setAnswers(parsedState.answers);
        setHasCompanyOwnedDealer(parsedState.hasCompanyOwnedDealer);
      }
    }
  }, [quizKey]);

  return (
    <OuterContainer>
      {value?.headline && <h2>{value.headline}</h2>}
      <Container>
        {quizCards?.length > 0 && (
          <QuizCardsContainer>
            {quizCards.map((card, index) => (
              <QuizCard
                key={card?._key || card?.headline}
                active={index === currentSection}
              >
                <Steps quizCards={quizCards} currentSection={currentSection} />

                {card?.headline && activeModule !== 'Question' && (
                  <h3>{card.headline}</h3>
                )}

                {card?.body?.blocks?.length > 0 && (
                  <BlockBuilder
                    blocks={card.body.blocks}
                    getInternalLink={getInternalLink}
                    Button={Button}
                  />
                )}

                {activeModule === 'Question' && (
                  <Questions
                    card={card}
                    answers={answers}
                    currentSection={currentSection}
                    handleAnswerChange={handleAnswerChange}
                  />
                )}

                {activeModule === 'Lead Capture' && (
                  <>
                    <LeadCapture
                      handleForm={handleForm}
                      errors={errors}
                      name={name}
                      setName={setName}
                      email={email}
                      setEmail={setEmail}
                      postalCode={postalCode}
                      setPostalCode={setPostalCode}
                    />
                    {card?.consent?.text?.length > 0 && (
                      <BlockBuilder
                        blocks={card.consent.text}
                        getInternalLink={getInternalLink}
                        Button={Button}
                      />
                    )}
                  </>
                )}

                {activeModule === 'Dealer Lookup' && (
                  <DealerLookup
                    handleDealerLookup={handleDealerLookup}
                    errors={errors}
                    postalCode={postalCode}
                    setPostalCode={setPostalCode}
                  />
                )}

                {activeModule === 'Recommendation' && (
                  <Recommendations
                    results={value?.results}
                    answers={answers}
                    hasCompanyOwnedDealer={hasCompanyOwnedDealer}
                    getInternalLink={getInternalLink}
                    noResultsFallback={value?.noResultsFallback}
                  />
                )}

                <ButtonsContainer>
                  {index !== 0 && (
                    <Button onClick={handleBack} fullWidth>
                      {value?.prevLabel || 'Back'}
                    </Button>
                  )}
                  {index !== quizCards.length - 1 && (
                    <Button
                      onClick={handleNext}
                      disabled={isLoading || nextButtonDisabled}
                      fullWidth
                    >
                      {isLoading ? 'Loading' : value?.nextLabel || 'Continue'}
                    </Button>
                  )}
                </ButtonsContainer>
              </QuizCard>
            ))}
          </QuizCardsContainer>
        )}
      </Container>
    </OuterContainer>
  );
}

BlockBuilderQuiz.propTypes = {
  value: PropTypes.shape({
    _key: PropTypes.string,
    headline: PropTypes.string,
    cards: PropTypes.arrayOf(
      PropTypes.shape({
        moduleType: PropTypes.string,
      })
    ),
    results: PropTypes.arrayOf(PropTypes.shape({})),
    noResultsFallback: PropTypes.shape({}),
    prevLabel: PropTypes.string,
    nextLabel: PropTypes.string,
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default BlockBuilderQuiz;
